import chunk from 'lodash/chunk'
import omit from 'lodash/omit'

import { FacebookSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { logInfo } from '$extensionSrc/utils/loggingUtils'
import { remoteLog } from '$extensionSrc/utils/remoteLog'

import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import * as fbUrls from './fbUrls'
import { FB_UNFRIEND_TIMEOUT } from '../../consts/stageMachineConsts'
import { updateRecommendation } from '../../state'
import { FBRecommendationKey, PLATFORM, StageType } from '../../utils/enums'
import { openModal } from '../../utils/fbUnfriend'
import { StageDef } from '../automationMachineInterfaces'
import {
  getStoredMachineOverlayStatus,
  setStoredMachineOverlayStatus,
} from '../automationStageMachine'
import { throwIfGlobalInterruptRequested } from '../globalInterrupt'
import * as promises from '../interruptiblePromises'
import { evaluateXpath } from '../interruptibleXpath'
import { InputData } from '../machineInterfaces'
import {
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import { getElement, getInitialValueBySettingKey } from '../utils'

const getCurrentValueFn = () => () => null
const getIsFulfilledFn = () => () => false
const getCantUndoFn = () => () => true
const getIsDisabledFn = () => defaultIsDisabledFn
const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const BATCH_SIZE = 100
const MAX_FRIENDS_INITIAL_GATHER = 300
const MAX_FRIENDS = 5000

export type Friend = {
  id: string
  isDeactivated: boolean
  isSelected: boolean
  name?: string
  numberOfMutualFriends: number
  photo?: string
  url?: string
}

export type FriendsLookup = Record<string, Friend>

export type Selected = Record<string, Friend>

export type Batch = {
  friends: Friend[]
  reviewed: boolean
  unfriended: Record<string, true>
}

export type State = {
  batches: Batch[]
  labeledTotal: number | undefined
  loadAllFriendsWasClicked: boolean
  remaining: number
  timing: {
    gather: number
    update: number
  }
  total: number
  unfriended: Record<string, true>
  unfriendedDuringMostRecentUpdate: Record<string, true>
}

type GatherStatus = {
  currentTotal: number
  labeledTotal: number
  stage: 'gather'
}

type UpdateStatus = {
  selectedTotal: number
  stage: 'update'
  unfriendedTotal: number
}

function getFriendFromSvgEl(svgEl: SVGElement): Friend | undefined {
  let isDeactivated = false
  let numberOfMutualFriends = 0

  let url: string | undefined
  const name = svgEl.ariaLabel
  let id = name

  const friendEl = evaluateXpath<HTMLAnchorElement>(
    svgEl,
    './/ancestor-or-self::a',
  )[0]
  if (friendEl) {
    url = friendEl.href
    const urlObject = new URL(url)
    id = urlObject.pathname.replace(/^\//, '')
    if (id === 'profile.php') {
      id = urlObject.searchParams.get('id')
    }
    const mutualFriendsEl = evaluateXpath<HTMLSpanElement>(
      friendEl,
      './/span[contains(text(), "mutual friend")]',
    )[0]
    if (mutualFriendsEl) {
      numberOfMutualFriends = parseInt(mutualFriendsEl.textContent, 10)
    }
  } else {
    isDeactivated = true
  }

  if (!id) {
    remoteLog.error('getFriendFromSvgEl: Could not determine ID for svgEl')
    return undefined
  }

  const imageEl = evaluateXpath<SVGImageElement>(
    svgEl,
    './/*[local-name()="image"]',
  )[0]
  const photo = imageEl.attributes.getNamedItem('xlink:href').value

  let friend: Friend = {
    id,
    isDeactivated,
    isSelected: false,
    numberOfMutualFriends,
  }
  if (name) {
    friend = { ...friend, name }
  }
  if (photo) {
    friend = { ...friend, photo }
  }
  if (url) {
    friend = { ...friend, url }
  }

  return friend
}

function getRemaining(
  labeledTotal: number | undefined,
  total: number,
  unfriended: Record<string, true>,
): number {
  if (!labeledTotal) {
    return 0
  }
  return labeledTotal - total - Object.keys(unfriended).length
}

function selectedIdsAreAllLoaded(
  loaded: Record<string, true>,
  selected: Selected,
): boolean {
  let i = 0
  for (const friend of Object.values(selected)) {
    i += 1
    if (loaded[friend.id] === undefined) {
      remoteLog.debug(
        `selectedIdsAreAllLoaded: friend.id = ${friend.id} is not loaded, i = ${i}`,
      )
      return false
    }
  }
  return true
}

function isElementInViewport(el: HTMLElement) {
  const rect = el.getBoundingClientRect()

  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  )
}

async function waitForLoadingToStop(
  allFriendsEl: HTMLDivElement,
): Promise<boolean> {
  const xpath = './/div[@aria-label="Loading..."]'
  const interval = 100
  let current = 0
  const max = 30000
  while (current < max) {
    const loadingEl = evaluateXpath<HTMLDivElement>(allFriendsEl, xpath)[0]
    if (!loadingEl) {
      // If the loading div isn't in the DOM, we've finished loading entirely.
      // There are no more friends to load.
      break
    }
    if (!isElementInViewport(loadingEl)) {
      // If the loading div is outside of the viewport, the current XHR load
      // has completed, but there are still more friends to load
      break
    }
    await promises.wait(interval)
    current += interval
  }
  const loadingEl = evaluateXpath<HTMLDivElement>(allFriendsEl, xpath)[0]
  if (!loadingEl) {
    // All friends are loaded
    return true
  }
  // There are more friends to load
  return false
}

async function updateOverlay(status: GatherStatus | UpdateStatus) {
  const currentStatus = await getStoredMachineOverlayStatus(PLATFORM.FACEBOOK)
  await setStoredMachineOverlayStatus(PLATFORM.FACEBOOK, {
    ...currentStatus,
    ...status,
  })
}

const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'FB_UNFRIEND_GATHER',
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.FB_UNFRIEND_GATHER,
    timeout: FB_UNFRIEND_TIMEOUT,
    url: fbUrls.friendsListUrl,
    action: async (_machineContext, userContext, additionalMachineData) => {
      remoteLog.debug('FB_UNFRIEND_GATHER: At top')
      const startTime = performance.now()

      const savedState: State | null = getInitialValueBySettingKey(
        userContext.initialValues,
        FacebookSettingKey.UNFRIEND,
      )

      let max = MAX_FRIENDS_INITIAL_GATHER

      const existingFriendIds: Record<string, true> = {}
      const friendsLookup: FriendsLookup = {}
      let batches: Batch[] = []

      const inputData: InputData | null = additionalMachineData.inputData
      const loadAllFriendsWasClicked =
        inputData?.loadAllFriendsWasClicked || false
      remoteLog.debug(
        `FB_UNFRIEND_GATHER: loadAllFriendsWasClicked = ${loadAllFriendsWasClicked}`,
      )

      if (loadAllFriendsWasClicked) {
        max = MAX_FRIENDS
        batches = savedState?.batches || []
        batches.forEach((b) => {
          b.friends.forEach((f) => {
            existingFriendIds[f.id] = true
          })
        })
        // This makes "load all friends" during dry runs a little more
        // realistic -- if we've already "unfriended" a friend as a part of the
        // initial gather of 300, don't add them to new batches during "load
        // all friends"
        if (savedState?.unfriended) {
          Object.keys(savedState.unfriended).forEach((friendId) => {
            existingFriendIds[friendId] = true
          })
        }
      }

      // Friends container
      const allFriendsEl = await getElement<HTMLDivElement>(
        document,
        '//div[@aria-label="All friends"]',
        // On slow connections, it can take some time for the "All friends"
        // sidebar to show up
        { timeout: 60000 },
      )
      remoteLog.debug('FB_UNFRIEND_GATHER: Got allFriendsEl')

      // Labeled total of friends
      let labeledTotal: number | undefined
      const h2El = evaluateXpath<HTMLHeadingElement>(allFriendsEl, './/h2')[0]
      if (h2El) {
        labeledTotal = parseInt(
          h2El.textContent.replace(/,/g, '').replace(/ friends/, ''),
          10,
        )
        remoteLog.debug(
          `FB_UNFRIEND_GATHER: Got labeledTotal = ${labeledTotal}`,
        )
      }

      // The div that scrolls
      const scrollyPaneEl = await getElement<HTMLDivElement>(
        allFriendsEl,
        './/div[@data-thumb="1"]/..',
      )
      remoteLog.debug('FB_UNFRIEND_GATHER: Got scrollyPaneEl')
      const scrollAmount = scrollyPaneEl.getBoundingClientRect().height * 3
      remoteLog.debug(`FB_UNFRIEND_GATHER: scrollAmount = ${scrollAmount}`)
      scrollyPaneEl.scrollTo(0, 0)
      remoteLog.debug('FB_UNFRIEND_GATHER: Scrolled to top')

      // Get initial friends
      const svgEls = evaluateXpath<SVGElement>(
        allFriendsEl,
        './/*[local-name()="svg"][@role="img"]',
      )
      remoteLog.debug(
        `FB_UNFRIEND_GATHER: Got svgEls.length = ${svgEls.length}`,
      )
      for (const svgEl of svgEls) {
        const friend = getFriendFromSvgEl(svgEl)
        if (friend) {
          friendsLookup[friend.id] = friend
        }
      }
      remoteLog.debug(
        `FB_UNFRIEND_GATHER: Got initial friends, Object.keys(friendsLookup).length = ${Object.keys(friendsLookup).length}`,
      )

      // Listen for additional friends that are added as we scroll
      const recurse = (el: HTMLElement | SVGElement) => {
        if (el.tagName === 'svg') {
          const svgEl: SVGElement = el as SVGElement
          const friend = getFriendFromSvgEl(svgEl)
          if (friend) {
            friendsLookup[friend.id] = friend
          }
        }
        if (el.childNodes) {
          ;[...el.childNodes].forEach(recurse)
        }
      }
      const mutationObserver = new MutationObserver((mutationRecords) => {
        for (const mutationRecord of mutationRecords) {
          for (const node of mutationRecord.addedNodes) {
            recurse(node as HTMLElement)
          }
        }
      })
      mutationObserver.observe(allFriendsEl, {
        childList: true,
        subtree: true,
      })
      remoteLog.debug(
        'FB_UNFRIEND_GATHER: Set up mutation observer, starting scroll',
      )

      // Scroll down
      let numVisibleNewFriends = -1
      let prevNumVisibleNewFriends = -1
      while (numVisibleNewFriends < max) {
        remoteLog.debug(
          `FB_UNFRIEND_GATHER: Top of while loop, numVisibleNewFriends = ${numVisibleNewFriends}, prevNumVisibleNewFriends = ${prevNumVisibleNewFriends}, max = ${max}`,
        )
        // Scroll backwards a little bit and wait before scrolling down as this
        // inspires Facebook to trigger the XHR on slow connections
        const backwardsScrollAmount = -(scrollAmount / 6)
        scrollyPaneEl.scrollBy(0, backwardsScrollAmount)
        remoteLog.debug(
          `FB_UNFRIEND_GATHER: Scrolled backwards ${backwardsScrollAmount}`,
        )
        await promises.wait(500)

        scrollyPaneEl.scrollBy(0, scrollAmount)
        remoteLog.debug(`FB_UNFRIEND_GATHER: Scrolled forwards ${scrollAmount}`)

        const allFriendsLoaded = await waitForLoadingToStop(allFriendsEl)
        remoteLog.debug(
          `FB_UNFRIEND_GATHER: Loading stopped, allFriendsLoaded = ${allFriendsLoaded}`,
        )

        prevNumVisibleNewFriends = numVisibleNewFriends
        numVisibleNewFriends = Object.keys(friendsLookup).length
        remoteLog.debug(
          `FB_UNFRIEND_GATHER: allFriendsLoaded = ${allFriendsLoaded}, numVisibleNewFriends = ${numVisibleNewFriends}, prevNumVisibleNewFriends = ${prevNumVisibleNewFriends}`,
        )

        await updateOverlay({
          currentTotal: Object.keys(friendsLookup).length,
          labeledTotal,
          stage: 'gather',
        })
        remoteLog.debug(
          `FB_UNFRIEND_GATHER: Updated overlay with currentTotal = ${Object.keys(friendsLookup).length}`,
        )
        if (
          allFriendsLoaded &&
          prevNumVisibleNewFriends === numVisibleNewFriends
        ) {
          remoteLog.debug(
            'FB_UNFRIEND_GATHER: breaking, allFriendsLoaded && prevNumVisibleNewFriends === numVisibleNewFriends',
          )
          break
        }

        throwIfGlobalInterruptRequested()
      }

      mutationObserver.disconnect()
      remoteLog.debug('FB_UNFRIEND_GATHER: Disconnected mutation observer')

      // Assemble new state
      const endTime = performance.now()
      const seconds = (endTime - startTime) / 1000
      const friends: Friend[] = Object.values(friendsLookup)
      batches = [
        ...batches,
        ...chunk(
          friends.filter((f) => existingFriendIds[f.id] === undefined),
          BATCH_SIZE,
        ).map((c) => ({
          friends: c,
          reviewed: false,
          unfriended: {},
        })),
      ]
      if (!loadAllFriendsWasClicked) {
        batches = batches.slice(0, 3)
      }

      let total = 0
      batches.forEach((b) => {
        total += b.friends.length
      })

      const unfriended = loadAllFriendsWasClicked
        ? savedState?.unfriended || {}
        : {}

      const remaining = getRemaining(labeledTotal, total, unfriended)
      remoteLog.debug(
        `FB_UNFRIEND_GATHER: Finished with scroll, total = ${total}, remaining = ${remaining}`,
      )

      const state: State = {
        batches,
        labeledTotal,
        loadAllFriendsWasClicked,
        remaining,
        timing: {
          gather: seconds,
          update: 0,
        },
        total,
        unfriended,
        unfriendedDuringMostRecentUpdate: {},
      }

      remoteLog.debug('FB_UNFRIEND_GATHER: Calling updateStoredInitialValues')
      await userContext.updateStoredInitialValues(
        { [FacebookSettingKey.UNFRIEND]: state },
        FBRecommendationKey.FB_UNFRIEND,
      )

      // Re-open unfriending modal on set 4 if the user clicked "Load all
      // friends" on Set 3 of the modal
      if (inputData?.openSet4) {
        remoteLog.debug('FB_UNFRIEND_GATHER: Opening Set 4 as directed')
        await openModal(
          // This is the index of Set 4
          3,
          userContext.userId,
          {},
        )
      }
      remoteLog.debug('FB_UNFRIEND_GATHER: At bottom')
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: 'FB_UNFRIEND_UPDATE',
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.FB_UNFRIEND_UPDATE,
    timeout: FB_UNFRIEND_TIMEOUT,
    url: fbUrls.friendsListUrl,
    action: async (machineContext, userContext, additionalMachineData) => {
      remoteLog.debug('FB_UNFRIEND_UPDATE: At top')
      const startTime = performance.now()

      await promises.wait(500)

      // Setup
      let state: State | null = getInitialValueBySettingKey(
        userContext.initialValues,
        FacebookSettingKey.UNFRIEND,
      )
      let selected: Selected = additionalMachineData.inputData.selected
      const selectedTotal = Object.keys(
        additionalMachineData.inputData.selected,
      ).length
      if (additionalMachineData.resumeData.data?.selected) {
        selected = additionalMachineData.resumeData.data.selected
      }
      let unfriendedDuringMostRecentUpdate: Record<string, true> = {}
      if (
        additionalMachineData.resumeData.data?.unfriendedDuringMostRecentUpdate
      ) {
        unfriendedDuringMostRecentUpdate =
          additionalMachineData.resumeData.data.unfriendedDuringMostRecentUpdate
      }
      let unfriended: Record<string, true> = state?.unfriended || {}
      if (additionalMachineData.resumeData.data?.unfriended !== undefined) {
        unfriended = additionalMachineData.resumeData.data.unfriended
      }

      const loaded: Record<string, true> = {}

      // Friends container
      const allFriendsEl = await getElement<HTMLDivElement>(
        document,
        '//div[@aria-label="All friends"]',
        // On slow connections, it can take some time for the "All friends"
        // sidebar to show up
        { timeout: 60000 },
      )
      remoteLog.debug('FB_UNFRIEND_UPDATE: Got allFriendsEl')

      // The div that scrolls
      const scrollyPaneEl = evaluateXpath<HTMLDivElement>(
        allFriendsEl,
        './/div[@data-thumb="1"]/..',
      )[0]
      remoteLog.debug('FB_UNFRIEND_UPDATE: Got scrollyPaneEl')
      const scrollAmount = scrollyPaneEl.getBoundingClientRect().height * 3
      remoteLog.debug(`FB_UNFRIEND_UPDATE: scrollAmount = ${scrollAmount}`)
      scrollyPaneEl.scrollTo(0, 0)
      remoteLog.debug('FB_UNFRIEND_UPDATE: Scrolled to top')

      // Get initial friends
      const svgEls = evaluateXpath<SVGElement>(
        allFriendsEl,
        './/*[local-name()="svg"][@role="img"]',
      )
      remoteLog.debug(
        `FB_UNFRIEND_UPDATE: Got svgEls.length = ${svgEls.length}`,
      )
      for (const svgEl of svgEls) {
        const friend = getFriendFromSvgEl(svgEl)
        if (friend) {
          loaded[friend.id] = true
        }
      }
      remoteLog.debug(
        `FB_UNFRIEND_UPDATE: Initial load done, Object.keys(loaded).length = ${Object.keys(loaded).length}`,
      )

      // Listen for additional friends that are added as we scroll
      const recurse = (el: HTMLElement | SVGElement) => {
        if (el.tagName === 'svg') {
          const svgEl: SVGElement = el as SVGElement
          const friend = getFriendFromSvgEl(svgEl)
          if (friend) {
            loaded[friend.id] = true
          }
        }
        if (el.childNodes) {
          ;[...el.childNodes].forEach(recurse)
        }
      }
      const mutationObserver = new MutationObserver((mutationRecords) => {
        for (const mutationRecord of mutationRecords) {
          for (const node of mutationRecord.addedNodes) {
            recurse(node as HTMLElement)
          }
        }
      })
      mutationObserver.observe(allFriendsEl, {
        childList: true,
        subtree: true,
      })
      remoteLog.debug(
        'FB_UNFRIEND_UPDATE: Set up mutation observer, starting scroll',
      )

      // Scroll down until all of the friends to be removed are visible

      function getScrollProgress(): { numLoaded: number; offset: number } {
        return {
          numLoaded: Object.keys(loaded).length,
          offset: scrollyPaneEl.scrollTop,
        }
      }

      let prevProgress = getScrollProgress()
      let nScrollUnchanged = 0
      while (!selectedIdsAreAllLoaded(loaded, selected)) {
        // Scroll backwards a little bit and wait before scrolling down as this
        // inspires Facebook to trigger the XHR on slow connections
        const backwardsScrollAmount = -(scrollAmount / 6)
        scrollyPaneEl.scrollBy(0, backwardsScrollAmount)
        remoteLog.debug(
          `FB_UNFRIEND_UPDATE: Scrolled backwards ${backwardsScrollAmount}`,
        )
        await promises.wait(500)

        scrollyPaneEl.scrollBy(0, scrollAmount)
        remoteLog.debug(`FB_UNFRIEND_UPDATE: Scrolled forwards ${scrollAmount}`)

        await waitForLoadingToStop(allFriendsEl)
        remoteLog.debug('FB_UNFRIEND_UPDATE: Loading stopped')

        // Check Progress
        const curProgress = getScrollProgress()
        if (
          prevProgress.numLoaded === curProgress.numLoaded &&
          prevProgress.offset === curProgress.offset
        ) {
          nScrollUnchanged += 1
        } else {
          nScrollUnchanged = 0
        }

        if (nScrollUnchanged === 5) {
          throw new Error(
            'Failed to scroll selected ids into view: scroll offset not changing, no new elements loaded',
          )
        }

        prevProgress = curProgress

        remoteLog.debug(
          `FB_UNFRIEND_UPDATE: Bottom of while loop, ` +
            `!selectedIdsAreAllLoaded: ${!selectedIdsAreAllLoaded(loaded, selected)}, ` +
            `offset: ${curProgress.offset}, ` +
            `nLoaded: ${curProgress.numLoaded}`,
        )
        throwIfGlobalInterruptRequested()
      }

      mutationObserver.disconnect()
      remoteLog.debug('FB_UNFRIEND_UPDATE: Disconnected mutation observer')

      // Do the unfriending
      remoteLog.debug(
        `FB_UNFRIEND_UPDATE: Starting unfriending loop, Object.values(selected).length = ${Object.values(selected).length}`,
      )
      for (const friend of Object.values(selected)) {
        let friendEl: HTMLAnchorElement | HTMLDivElement | undefined

        // Do not fail unfriending loop if 1 friend runs into an error
        try {
          if (!friend.isDeactivated) {
            friendEl = await getElement<HTMLAnchorElement>(
              document,
              `//a[@href="${friend.url}"][@role="link"]`,
              { noDebug: true },
            )
          } else {
            const xpath =
              `//*[local-name()="svg"][@aria-label="${friend.name}"]` +
              '[@role="img"]/ancestor-or-self::div[@role="button"]'
            friendEl = await getElement<HTMLDivElement>(document, xpath, {
              noDebug: true,
            })
          }

          const moreEl = await getElement<HTMLElement>(
            friendEl,
            './/div[@aria-label="More"]',
            { noDebug: true },
          )

          moreEl.click()
          const unfriendEl = await getElement<HTMLDivElement>(
            document,
            '//span[contains(text(), "Unfriend")]/ancestor-or-self::div[@role="menuitem"]',
            // This menu's contents are loaded via XHR so we have to wait
            // longer than 5 seconds on slow connections
            { noDebug: true, timeout: 30000 },
          )
          unfriendEl.click()
          const cancelEl = await getElement<HTMLDivElement>(
            document,
            '//div[@aria-label="Cancel"][@role="button"]',
            { noDebug: true },
          )
          const confirmEl = await getElement<HTMLDivElement>(
            document,
            '//div[@aria-label="Confirm"][@role="button"]',
            { noDebug: true },
          )

          throwIfGlobalInterruptRequested()
          if (userContext.isDry) {
            cancelEl.click()
          } else {
            confirmEl.click()
          }
        } catch (error) {
          logInfo(
            `Failed to unfriend in loop due to error=${error}.`,
            'fbUnfriend',
          )
        }

        unfriended[friend.id] = true
        unfriendedDuringMostRecentUpdate[friend.id] = true

        await userContext.incrementTotalCount(FBRecommendationKey.FB_UNFRIEND)

        selected = omit(selected, friend.id)

        // Update resume data
        await additionalMachineData.resumeData.set({
          selected,
          unfriended,
          unfriendedDuringMostRecentUpdate,
        })

        // Assemble new state
        let total = 0
        const batches = state.batches.map((b) => {
          const unfriendedInThisBatch = { ...b.unfriended }
          b.friends.forEach((f) => {
            if (unfriended[f.id] !== undefined) {
              unfriendedInThisBatch[f.id] = true
            }
          })
          const friends = b.friends.filter(
            (f) => unfriended[f.id] === undefined,
          )
          total += friends.length
          let reviewed = b.reviewed
          if (friends.length === 0) {
            reviewed = true
          }
          return {
            friends,
            reviewed,
            unfriended: unfriendedInThisBatch,
          }
        })

        state = {
          ...state,
          batches,
          remaining: getRemaining(state?.labeledTotal, total, unfriended),
          total,
          unfriended,
          unfriendedDuringMostRecentUpdate,
        }

        // We use this instead of userContext.updateStoredInitialValues because
        // that function sets isUpdated to false, which generates an errant
        // recommendation:undoMarkAsDone event
        await updateRecommendation(
          PLATFORM.FACEBOOK,
          userContext.userId,
          FBRecommendationKey.FB_UNFRIEND,
          {
            initialState: state,
          },
        )

        // Update overlay
        await updateOverlay({
          selectedTotal,
          stage: 'update',
          unfriendedTotal: Object.keys(unfriendedDuringMostRecentUpdate).length,
        })

        await userContext.updateStatisticsLastCount(
          FBRecommendationKey.FB_UNFRIEND,
          Object.keys(unfriendedDuringMostRecentUpdate).length,
        )
      }
      remoteLog.debug('FB_UNFRIEND_UPDATE: Finished unfriending loop')

      // Final update of local storage to record the update duration
      const endTime = performance.now()
      const seconds = (endTime - startTime) / 1000
      state = {
        ...state,
        timing: {
          ...state.timing,
          update: seconds,
        },
      }
      await updateRecommendation(
        PLATFORM.FACEBOOK,
        userContext.userId,
        FBRecommendationKey.FB_UNFRIEND,
        {
          initialState: state,
        },
      )

      // Re-open unfriending modal
      await openModal(
        additionalMachineData.inputData.index,
        userContext.userId,
        unfriendedDuringMostRecentUpdate,
      )
      remoteLog.debug('FB_UNFRIEND_UPDATE: At bottom')
    },
  },
]

const undoStageDefs: StageDef[] = []

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
